// V2.1 Digital Passport — primary lifecycle view for one laptop.
//
// Pure consumer of GET /api/passports/{id}/digital-passport. Every slice
// (header + summaries + qc/battery/repair/warranty history + timeline) is
// rendered from that single response — no per-tab API calls.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Battery, ClipboardCheck, Wrench, ShieldCheck,
  Activity, Info, Search, Loader2, ChevronRight, Cpu, MemoryStick, HardDrive,
} from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { displaySerial } from "@/lib/serials";
import { formatRam, formatStorage } from "@/lib/specs";

const TABS = [
  { key: "overview",  label: "Overview",         icon: Info },
  { key: "qc",        label: "Purchase QC",      icon: ClipboardCheck },
  { key: "battery",   label: "Battery History",  icon: Battery },
  { key: "repair",    label: "Repair History",   icon: Wrench },
  { key: "warranty",  label: "Warranty History", icon: ShieldCheck },
  { key: "timeline",  label: "Activity Timeline",icon: Activity },
];

const STATUS_TONE = {
  AVAILABLE: "bg-emerald-100 text-emerald-800",
  RESERVED: "bg-amber-100 text-amber-800",
  ALLOCATED: "bg-amber-100 text-amber-800",
  PACKED: "bg-sky-100 text-sky-800",
  SHIPPED: "bg-sky-100 text-sky-800",
  DELIVERED: "bg-slate-200 text-slate-800",
  SOLD: "bg-slate-200 text-slate-800",
  RETURNED: "bg-rose-100 text-rose-800",
  QC_PENDING: "bg-blue-100 text-blue-800",
  QC_PASSED: "bg-emerald-100 text-emerald-800",
  VENDOR_CASE: "bg-rose-100 text-rose-800",
  REPAIR: "bg-orange-100 text-orange-800",
  SERVICE: "bg-orange-100 text-orange-800",
  WARRANTY_INSPECTION: "bg-orange-100 text-orange-800",
  SCRAPPED: "bg-slate-300 text-slate-700",
  LOST: "bg-slate-300 text-slate-700",
};

const fmtDate = (v) => v ? new Date(v).toLocaleString() : "—";

export default function DigitalPassportPage() {
  const { passport_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("overview");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data: d } = await api.get(`/passports/${passport_id}/digital-passport?activity_limit=500`);
      setData(d);
    } catch (e) { setErr(errorToMessage(e, "Failed to load Digital Passport")); }
    finally { setLoading(false); }
  }, [passport_id]);
  useEffect(() => { load(); }, [load]);

  const doSearch = async () => {
    const q = search.trim();
    if (!q) return;
    try {
      const { data: inv } = await api.get(`/inventory?q=${encodeURIComponent(q)}&page=1&size=5`);
      const first = inv?.items?.[0];
      if (first?.passport_id) { navigate(`/admin/passports/${first.passport_id}`); }
      else setErr(`No passport matches "${q}"`);
    } catch (e) { setErr(errorToMessage(e, "Search failed")); }
  };

  if (loading) return <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading Digital Passport…</div>;
  if (err && !data) return <div className="text-rose-700">{err}</div>;
  if (!data) return null;

  const h = data.header || {};
  return (
    <div className="space-y-4" data-testid="digital-passport-page">
      {/* Search + back */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-600 inline-flex items-center gap-1">
          <ArrowLeft size={14}/> Back
        </button>
        <div className="flex-1 flex items-center gap-2">
          <Search size={14} className="text-slate-400"/>
          <input
            placeholder="Open another Passport — enter Passport ID (RP…) or Manufacturer Serial"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
            className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-slate-800 font-mono"
            data-testid="dp-search"
          />
        </div>
      </div>

      {err && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}

      {/* Sticky header */}
      <section className="sticky top-0 z-10 bg-white border border-slate-200 rounded-md p-4 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Passport Number</div>
            <div className="text-2xl font-bold text-slate-900 font-mono" data-testid="dp-passport-uid">
              {h.passport_uid || h.passport_no || "—"}
            </div>
            {h.legacy_passport_no && (
              <div className="mt-1 inline-flex items-center rounded bg-slate-100 border border-slate-200 px-2 py-0.5 text-[10px] font-mono text-slate-500"
                   data-testid="dp-legacy-passport-no"
                   title="Legacy Passport Number (pre-migration 028)">
                Legacy: {h.legacy_passport_no}
              </div>
            )}
            {h.item_type && h.item_type !== "laptop" && (
              <div className="ml-2 inline-flex items-center rounded-full bg-indigo-100 border border-indigo-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-800"
                   data-testid="dp-item-type">
                {h.item_type}
              </div>
            )}
            <div className="text-xs text-slate-500 font-mono mt-1">SN: {displaySerial(h.manufacturer_serial)}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-slate-400">Current Status</div>
            <span className={`inline-block mt-1 rounded-full px-3 py-1 text-sm font-semibold ${STATUS_TONE[h.current_status] || "bg-slate-100 text-slate-800"}`}>
              {h.current_status || "—"}
            </span>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-sm">
          <HdrTile label="Brand" value={h.brand}/>
          <HdrTile label="Model" value={h.model}/>
          <HdrTile label="CPU" value={h.processor} icon={Cpu}/>
          <HdrTile label="RAM" value={h.ram_gb != null ? formatRam(h.ram_gb) : null} icon={MemoryStick}/>
          <HdrTile label="Storage" value={h.storage_gb != null ? formatStorage(h.storage_gb, h.storage_type) : null} icon={HardDrive}/>
          <HdrTile label="Location" value={h.current_location}/>
          <HdrTile label="Purchase" value={h.purchase_no}/>
          <HdrTile label="Vendor" value={h.vendor_name}/>
          <HdrTile label="Warranty" value={data.warranty_summary?.warranty_status}/>
          <HdrTile label="Battery Health" value={data.battery_summary?.latest_health_pct != null ? `${data.battery_summary.latest_health_pct}%` : null} icon={Battery}/>
          <HdrTile label="Last QC" value={data.qc_summary?.last_decision}/>
          <HdrTile label="Available Since" value={fmtDate(h.available_since)}/>
        </div>
      </section>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2 whitespace-nowrap ${
                active ? "border-slate-900 text-slate-900 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`dp-tab-${t.key}`}
            >
              <Icon size={14}/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewTab data={data}/>}
      {tab === "qc" && <QCTab items={data.qc_history || []}/>}
      {tab === "battery" && <BatteryTab items={data.battery_history || []}/>}
      {tab === "repair" && <RepairTab items={data.repair_history || []}/>}
      {tab === "warranty" && <WarrantyTab items={data.warranty_history || []}/>}
      {tab === "timeline" && <TimelineTab items={data.activity_timeline || []}/>}
    </div>
  );
}

function HdrTile({ label, value, icon: Icon }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
        {Icon && <Icon size={10}/>} {label}
      </div>
      <div className="text-sm font-medium text-slate-900 truncate">{value || "—"}</div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">{title}</div>
      {children}
    </section>
  );
}

function OverviewTab({ data }) {
  const h = data.header || {};
  const bs = data.battery_summary || {};
  const ws = data.warranty_summary || {};
  const qs = data.qc_summary || {};
  const inv = data.inventory_status || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card title="Current Status" testid="ov-current">
        <div className="text-lg font-semibold">{h.current_status || "—"}</div>
        <div className="text-xs text-slate-500 mt-1">Location: {h.current_location || "—"}</div>
      </Card>
      <Card title="Current Battery" testid="ov-battery">
        <div className="text-lg font-semibold">{bs.latest_health_pct != null ? `${bs.latest_health_pct}%` : "—"}</div>
        <div className="text-xs text-slate-500 mt-1">
          {bs.total_tests || 0} test(s) · Last: {fmtDate(bs.latest_test)}
          {bs.latest_cycle_count != null && ` · ${bs.latest_cycle_count} cycles`}
        </div>
      </Card>
      <Card title="Warranty" testid="ov-warranty">
        <div className="text-lg font-semibold">{ws.warranty_status || "No active warranty"}</div>
        <div className="text-xs text-slate-500 mt-1">
          {ws.warranty_start ? `From ${ws.warranty_start} to ${ws.warranty_end}` : "—"}
        </div>
      </Card>
      <Card title="Purchase Information" testid="ov-purchase">
        <div className="text-lg font-semibold font-mono">{h.purchase_no || "—"}</div>
        <div className="text-xs text-slate-500 mt-1">
          Vendor: {h.vendor_name || "—"} · Date: {h.purchase_date || "—"}
        </div>
      </Card>
      <Card title="Inventory Information" testid="ov-inventory">
        <div className="text-sm">
          {["warehouse","rack","shelf","bin"].map((k) => inv[k]).filter(Boolean).join(" / ") || "—"}
        </div>
        <div className="text-xs text-slate-500 mt-1">Since: {fmtDate(h.available_since)}</div>
      </Card>
      <Card title="Sales Information" testid="ov-sales">
        <div className="text-lg font-semibold font-mono">{inv.order_internal_no || "—"}</div>
        <div className="text-xs text-slate-500 mt-1">Customer: {inv.reserved_by_customer || "—"}</div>
      </Card>
      <Card title="Last Purchase QC" testid="ov-qc">
        <div className="text-lg font-semibold">{qs.last_decision || "—"}</div>
        <div className="text-xs text-slate-500 mt-1">
          {qs.total_records || 0} record(s) · Last: {fmtDate(qs.last_completed_at)}
        </div>
      </Card>
      {/* V2.9.1 — Item-type-specific QC values captured during the latest
          QC record. Renders only for non-laptop item types (RAM/SSD/Battery/
          Charger). All values come from qc_history[0] + its checklist
          `_item_type_data` blob — no extra endpoint needed. */}
      <QCCapturedCard header={h} qcHistory={data.qc_history || []} />
    </div>
  );
}

function QCCapturedCard({ header, qcHistory }) {
  const itype = (header?.item_type || "laptop").toLowerCase();
  if (itype === "laptop") return null;
  const latest = qcHistory[0] || {};
  const cd = latest.checklist || {};
  const itd = cd._item_type_data || {};
  const rows = [];
  if (itype === "ram") {
    const freq = itd.ram_frequency_mhz;
    const wmt = itd.ram_windows_memory_test_pass;
    rows.push(["Frequency", freq != null ? `${freq} MHz` : "—"]);
    rows.push(["Windows Memory Test", wmt === true ? "Passed" : wmt === false ? "Failed" : "—"]);
  } else if (itype === "ssd") {
    const health = latest.ssd_health_pct;
    const bs = itd.ssd_bad_sectors;
    rows.push(["Health", health != null ? `${health}%` : "—"]);
    rows.push(["Bad Sectors", bs === true ? "YES" : bs === false ? "NO" : "—"]);
  } else if (itype === "battery") {
    rows.push(["Health", latest.battery_health_pct != null ? `${latest.battery_health_pct}%` : "—"]);
    // Battery Backup + Design Capacity live in the checklist blob keyed by
    // the checklist item codes (Laptop) OR were captured via the Battery QC
    // form — try both locations for robustness.
    const backup = latest.battery_backup_min ?? itd.battery_backup_min;
    const design = itd.battery_design_capacity_wh;
    rows.push(["Backup Time", backup != null ? `${backup} min` : "—"]);
    rows.push(["Design Capacity", design != null ? `${design} Wh` : "—"]);
  } else if (itype === "adapter" || itype === "charger") {
    const ct = itd.charger_charging_test_pass;
    rows.push(["Charging Test", ct === true ? "Passed" : ct === false ? "Failed" : "—"]);
  } else {
    return null;
  }
  return (
    <Card title={`${itype.toUpperCase()} QC — Captured Values`} testid="ov-qc-captured">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <div className="text-slate-500">{label}</div>
            <div className="text-slate-900 font-medium text-right">{value}</div>
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
}

function QCTab({ items }) {
  return (
    <div className="space-y-4">
      <Card title="Purchase QC History" testid="qc-history">
        {items.length === 0 ? <div className="text-sm text-slate-500">No Purchase QC records yet.</div> : (
          <div className="space-y-2">
            {items.map((r) => (
              <div key={r.id} className="border border-slate-100 rounded-md p-3">
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${r.decision === "PASS" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                    {r.decision || "PENDING"}
                  </span>
                  <span className="text-xs text-slate-500">{fmtDate(r.completed_at)}</span>
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  Engineer: {r.engineer_name || "—"}
                  {r.battery_health_pct != null && ` · Battery ${r.battery_health_pct}%`}
                  {r.ssd_health_pct != null && ` · SSD ${r.ssd_health_pct}%`}
                  {r.cosmetic_grade && ` · Grade ${r.cosmetic_grade}`}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card title="Sales QC History" testid="sales-qc-history">
        <div className="text-sm text-slate-500">
          Sales QC history will appear here as separate records. Purchase QC and Sales QC are architecturally isolated — one never overwrites the other.
        </div>
      </Card>
    </div>
  );
}

function BatteryTab({ items }) {
  return (
    <Card title={`Battery History (${items.length})`} testid="battery-history">
      {items.length === 0 ? <div className="text-sm text-slate-500">No battery tests yet.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase">
              <tr><th className="text-left py-1">Date</th><th className="text-right">Health</th><th className="text-right">Cycles</th><th className="text-right">Backup</th><th className="text-right">Voltage</th><th className="text-left">Engineer</th><th className="text-left">Remarks</th></tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="py-2">{fmtDate(r.performed_at)}</td>
                  <td className="text-right tabular-nums">{r.battery_health_pct != null ? `${r.battery_health_pct}%` : "—"}</td>
                  <td className="text-right tabular-nums">{r.cycle_count ?? "—"}</td>
                  <td className="text-right tabular-nums">{r.backup_minutes != null ? `${r.backup_minutes} min` : "—"}</td>
                  <td className="text-right tabular-nums">{r.battery_voltage ?? "—"}</td>
                  <td>{r.performed_by_name || "—"}</td>
                  <td className="text-slate-500">{r.remarks || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function RepairTab({ items }) {
  return (
    <Card title={`Repair History (${items.length})`} testid="repair-history">
      {items.length === 0 ? <div className="text-sm text-slate-500">No repair records yet.</div> : (
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} className="border border-slate-100 rounded-md p-3">
              <div className="flex justify-between text-xs">
                <span className="font-mono text-slate-700">{r.case_no || "—"}</span>
                <span className="text-slate-500">{r.status || "—"}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Started {fmtDate(r.started_at)} · Completed {fmtDate(r.completed_at)}
              </div>
              {r.notes && <div className="text-sm text-slate-700 mt-1">{r.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function WarrantyTab({ items }) {
  return (
    <Card title={`Warranty History (${items.length})`} testid="warranty-history">
      {items.length === 0 ? <div className="text-sm text-slate-500">No warranty records yet.</div> : (
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} className="border border-slate-100 rounded-md p-3 flex items-center justify-between">
              <div>
                <div className="font-mono text-sm">{r.status}</div>
                <div className="text-xs text-slate-500">{r.start_date} → {r.end_date}</div>
              </div>
              <span className="text-xs text-slate-500">{fmtDate(r.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// Preset labels for QC reversal presets. Keep in sync with the dropdown
// in QCJobPage.jsx. Tuple: [display label, tailwind badge classes].
const QC_REVERSAL_PRESET_META = {
  wrong_button:     ["Wrong button",       "bg-amber-100 text-amber-800 border-amber-200"],
  wrong_unit:       ["Wrong unit",         "bg-rose-100  text-rose-800  border-rose-200"],
  spec_mismatch:    ["Spec mismatch",      "bg-indigo-100 text-indigo-800 border-indigo-200"],
  retest_different: ["Re-test differed",   "bg-sky-100    text-sky-800    border-sky-200"],
  data_entry_error: ["Data entry error",   "bg-slate-200  text-slate-800  border-slate-300"],
  other:            ["Other",              "bg-slate-100  text-slate-700  border-slate-200"],
};

// Extract `[preset_code]` from either metadata_json.reason or description.
function extractReversalPreset(e) {
  const raw = (e?.metadata_json?.reason) || e?.description || "";
  const m = /\[([a-z_]+)\]/i.exec(raw);
  if (!m) return null;
  const code = m[1].toLowerCase();
  return QC_REVERSAL_PRESET_META[code] ? { code, ...{} } : null;
}

function TimelineTab({ items }) {
  const [openId, setOpenId] = useState(null);
  return (
    <Card title={`Activity Timeline (${items.length})`} testid="activity-timeline">
      {items.length === 0 ? <div className="text-sm text-slate-500">No activity yet.</div> : (
        <ol className="relative border-l border-slate-200 ml-2 space-y-3">
          {items.map((e) => {
            const preset = e.event_code === "QC_DECISION_CORRECTED"
              ? extractReversalPreset(e) : null;
            const meta = preset ? QC_REVERSAL_PRESET_META[preset.code] : null;
            const isReversal = e.event_code === "QC_DECISION_CORRECTED";
            return (
            <li key={e.id} className="ml-4">
              <div className={`absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full ${isReversal ? "bg-amber-500 ring-2 ring-amber-200" : "bg-slate-400"}`}></div>
              <button
                onClick={() => setOpenId(openId === e.id ? null : e.id)}
                className="w-full text-left"
                data-testid={`tl-event-${e.event_code}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                    <ChevronRight size={12} className={`transition-transform ${openId === e.id ? "rotate-90" : ""}`}/>
                    {e.event_label}
                    {isReversal && (
                      <span className="inline-flex items-center rounded-full bg-amber-100 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800"
                            data-testid="tl-reversal-tag">
                        QC Correction
                      </span>
                    )}
                    {meta && (
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${meta[1]}`}
                            data-testid={`tl-reversal-preset-${preset.code}`}>
                        {meta[0]}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{fmtDate(e.occurred_at)}</div>
                </div>
                <div className="text-xs text-slate-500 ml-4">
                  {e.department || "—"} · {e.actor_role || "system"}
                  {e.passport_no && ` · ${e.passport_no}`}
                </div>
                {e.description && <div className="text-xs text-slate-600 ml-4 mt-1">{e.description}</div>}
                {openId === e.id && (
                  <div className="ml-4 mt-2 text-xs text-slate-500 border-t border-slate-100 pt-2 space-y-0.5">
                    <div>Event Code: <span className="font-mono">{e.event_code}</span></div>
                    {isReversal && e.metadata_json && (
                      <>
                        {e.metadata_json.previous_status && (
                          <div>Previous QC: <span className="font-mono">{e.metadata_json.previous_status}</span> → <span className="font-mono">{e.metadata_json.new_status}</span></div>
                        )}
                        {e.metadata_json.reason && (
                          <div>Reason: <span className="italic">{e.metadata_json.reason}</span></div>
                        )}
                      </>
                    )}
                    {e.manufacturer_serial && <div>Serial: <span className="font-mono">{e.manufacturer_serial}</span></div>}
                    {e.reference_type && <div>Reference: {e.reference_type} · <span className="font-mono">{e.reference_id}</span></div>}
                    {e.purchase_id && <div>Purchase: <span className="font-mono">{e.purchase_id}</span></div>}
                    {e.sales_order_id && <div>Sales Order: <span className="font-mono">{e.sales_order_id}</span></div>}
                    {e.warranty_id && <div>Warranty: <span className="font-mono">{e.warranty_id}</span></div>}
                    {e.case_id && <div>Case: <span className="font-mono">{e.case_id}</span></div>}
                  </div>
                )}
              </button>
            </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
